import { Injectable } from '@angular/core'
import { from, of, Observable, BehaviorSubject, combineLatest, throwError } from 'rxjs'
import { tap, catchError, concatMap, shareReplay, flatMap, first, skipWhile, map } from 'rxjs/operators'
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router'
import { HttpClient } from '@angular/common/http'
import createAuth0Client, { Auth0Client, IdToken } from '@auth0/auth0-spa-js'
import { environment } from '../../../environments/environment'
import { Organization, User } from '../shared.interfaces'

@Injectable()
export class UserService {
  private _config
  private _userAuthToken = new BehaviorSubject<any>(null)
  get userAuthToken() {
    return this._userAuthToken.value
  }
  user$ = this._userAuthToken.asObservable()

  private _dkeUser = new BehaviorSubject<User>(null)
  get dkeUser$() {
    return this._dkeUser.asObservable()
  }
  get user() {
    return this._dkeUser.value
  }

  public routeTo: string
  auth0Client$: Observable<Auth0Client>
  isAuthenticated$
  handleRedirectCallback$
  loggedIn: boolean = null

  constructor(
    private httpClient: HttpClient,
    private router: Router,
  ) {
    this._config = environment.auth0Config
    this.initializeAuth0()
    this.initializeUserSubscription()
  }

  /* istanbul ignore next */
  private initializeAuth0(): void {
    this.auth0Client$ = (
      from(
        createAuth0Client({
          domain: this._config.domain,
          client_id: this._config.clientID,
          redirect_uri: this._config.redirectUri,
          audience: this._config.audience,
          scope: this._config.scope,
          cacheLocation: 'memory',
        }),
      ) as Observable<Auth0Client>
    ).pipe(
      shareReplay(1), // Every subscription receives the same shared value
      catchError((err) => {
        console.error('Error creating Auth0 client:', err)
        return throwError(err)
      }),
    )
    this.isAuthenticated$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => {
        return from(client.isAuthenticated())
      }),
      tap((res) => {
        this.loggedIn = res
      }),
      catchError((err) => {
        console.error('Error checking authentication:', err)
        return of(false)
      }),
    )
    this.handleRedirectCallback$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.handleRedirectCallback())),
    )
  }

  /* istanbul ignore next */
  private initializeUserSubscription(): void {
    this.user$
      .pipe(
        flatMap((user) => {
          return user == null ? of(null as User) : this.createUser(user)
        }),
      )
      .subscribe((user) => {
        if (user) {
          this._dkeUser.next(user)
        }
      })
  }
  private static getAllegionOrganization(user: User): Organization {
    return user.organizations.find((g) => g.name.toLowerCase() === 'allegion')
  }
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | Promise<boolean | UrlTree> | boolean {
    return this.isAuthenticated$.pipe(
      tap((loggedIn) => {
        if (!loggedIn) {
          this.login(state.url)
        }
      }),
      first(),
    )
  }

  public isAllegionKeyPersonnel(): Observable<boolean> {
    return this._dkeUser.pipe(
      skipWhile((user) => user == null),
      first(),
      map((user) => {
        const allegionOrganization = UserService.getAllegionOrganization(user)
        if (
          allegionOrganization &&
          user.organizationUsers.find((g) => g.organizationId === allegionOrganization.id && g.permissionId === 4)
        ) {
          return true
        }
        return false
      }),
    )
  }

  private createUser(profile: any): Observable<User> {
    const user = {
      firstName: profile.given_name,
      lastName: profile.family_name,
      email: profile.email,
    }

    return this.httpClient.post(`${environment.usersUrl}`, user).pipe(
      map((response) => {
        return response as User
      }),
    )
  }

  getUser(userEmail: string): Observable<User> {
    return this.httpClient.get<User>(`${environment.usersUrl}?email=${encodeURIComponent(userEmail.replace('+', ' '))}`)
  }

  getTokenSilently$(options?: any): Observable<string> {
    let curClient: Auth0Client
    // Call getTokenSilently on every request.
    // need to return idToken raw until overtur uses access token.
    return this.auth0Client$.pipe(
      flatMap((client: Auth0Client) => {
        curClient = client
        return curClient.getTokenSilently()
      }),
    )
  }

  private getUser$(options?: any): Observable<any> {
    // @ts-ignore
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser())),
      tap((user) => this._userAuthToken.next(user)),
    )
  }

  loginRedirect(): void {
    if (this.routeTo) {
      this.router.navigate([this.routeTo])
    } else {
      this.router.navigate([this._config.inAppRedirects.loggedIn])
    }
  }

  localAuthSetup() {
    // This should only be called on app initialization
    // Set up local authentication streams
    const checkAuth$ = this.isAuthenticated$.pipe(
      concatMap((loggedIn: boolean) => {
        if (loggedIn) {
          // If authenticated, get user and set in app
          // NOTE: you could pass options here if needed
          return this.getUser$()
        }
        // If not authenticated, return stream that emits 'false'
        return of(loggedIn)
      }),
    )
    checkAuth$.subscribe({
      error: (err) => console.error('localAuthSetup error:', err),
    })
  }

  /* istanbul ignore next */
  login(redirectPath: string) {
    localStorage.setItem('redirectUrl', redirectPath)

    this.auth0Client$.subscribe((client: Auth0Client) => {
      client.loginWithRedirect({
        redirect_uri: this._config.redirectUri,
        appState: { target: redirectPath },
        scope: this._config.scope,
      })
    })
  }

  /* istanbul ignore next */
  handleAuthCallback() {
    const params = window.location.search

    // Check if there's an error in the URL
    if (params.includes('error=')) {
      const urlParams = new URLSearchParams(params)
      console.error('Auth0 error:', urlParams.get('error'), urlParams.get('error_description'))
    }

    if (params.includes('code=') && params.includes('state=')) {
      let targetRoute: string // Path to redirect to after login is processed

      const authComplete$ = this.handleRedirectCallback$.pipe(
        tap((cbRes: any) => {
          // Retrieve the target route from appState (if available)
          targetRoute = cbRes.appState && cbRes.appState.target ? cbRes.appState.target : '/'
          // targetRoute = '/user-permissions/138037'

          // Retrieve redirect URL from localStorage
          const storedRedirectUrl = localStorage.getItem('redirectUrl')

          // Use localStorage value if appState.target is not set
          targetRoute = storedRedirectUrl || targetRoute

          // Clear localStorage after using the redirect URL
          localStorage.removeItem('redirectUrl')
        }),
        concatMap(() => {
          // Get user information and authentication status
          return combineLatest([this.getUser$(), this.isAuthenticated$])
        }),
      )

      authComplete$.subscribe({
        next: ([user, loggedIn]) => {
          // Navigate to the target route after callback processing
          this.router.navigateByUrl(targetRoute)
        },
        error: (err) => {
          console.error('Auth callback error:', err)
        },
      })
    }
  }

  logout() {
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log out
      client.logout({
        client_id: this._config.clientID,
        returnTo: this._config.logoutUrl,
        federated: true,
      })
    })
  }

  getTokenSilently(): Observable<any> {
    return this.auth0Client$.pipe(
      flatMap((client: Auth0Client) => {
        return from(
          client.getTokenSilently({
            audience: this._config.audience,
            scope: this._config.scope,
            redirect_uri: this._config.redirectUri,
          }),
        )
      }),
    )
  }
}
